import { Component, type ReactNode } from 'react'
import { Sentry } from '@/sentry'
import { useTranslation } from 'react-i18next'

interface Props {
    children: ReactNode
    fallback?: ReactNode
}

interface State {
    hasError: boolean
    error?: Error
}

class ErrorBoundaryClass extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: any) {
        // Log to console in development
        if (import.meta.env.DEV) {
            console.error('Error caught by boundary:', error, errorInfo)
        }

        // Send to Sentry in all environments
        Sentry.captureException(error, {
            contexts: {
                react: {
                    componentStack: errorInfo.componentStack,
                },
            },
        })
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback
            }

            // Default error UI
            return <DefaultErrorFallback error={this.state.error} />
        }

        return this.props.children
    }
}

function DefaultErrorFallback({ error }: { error?: Error }) {
    const { t } = useTranslation()

    const handleReload = () => {
        window.location.reload()
    }

    const handleReportFeedback = () => {
        const eventId = Sentry.lastEventId()
        if (eventId) {
            Sentry.showReportDialog({ eventId })
        }
    }

    return (
        <div className="flex min-h-screen items-center justify-center bg-background p-4">
            <div className="w-full max-w-md space-y-6 text-center">
                <div className="space-y-2">
                    <h1 className="text-4xl font-bold text-destructive">
                        {t('error_boundary_title', 'خطأ غير متوقع')}
                    </h1>
                    <p className="text-muted-foreground">
                        {t('error_boundary_description', 'عذراً، حدث خطأ غير متوقع. يرجى إعادة تحميل الصفحة أو الإبلاغ عن المشكلة.')}
                    </p>
                    {import.meta.env.DEV && error && (
                        <div className="mt-4 rounded-lg bg-destructive/10 p-4 text-left text-sm">
                            <p className="font-semibold text-destructive">{error.name}</p>
                            <p className="mt-1 font-mono text-xs text-muted-foreground">
                                {error.message}
                            </p>
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                    <button
                        onClick={handleReload}
                        className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
                    >
                        {t('error_boundary_reload', 'إعادة تحميل الصفحة')}
                    </button>

                    <button
                        onClick={handleReportFeedback}
                        className="rounded-md border border-input bg-background px-6 py-2 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
                    >
                        {t('error_boundary_report', 'الإبلاغ عن المشكلة')}
                    </button>
                </div>
            </div>
        </div>
    )
}

// Export the error boundary
export const ErrorBoundary = ErrorBoundaryClass
